import { useState, useCallback } from 'react';
import { supabase } from '../supabase/client';
import type { TargetAudienceFilter } from '../types/Campaign';

export interface TargetAudienceData {
  type?: 'csv_upload' | 'generic_filter';
  filter?: TargetAudienceFilter;
  fileUrl?: string;
  fileName?: string;
}

export interface CSVPreviewRow {
  row: string | number;
  [key: string]: string | number;
}

export interface UseTargetAudienceReturn {
  uploadCSV: (file: File, campaignId: string) => Promise<string>;
  updateCampaignTargetAudience: (campaignId: string, data: TargetAudienceData) => Promise<void>;
  validateCSVFile: (file: File) => Promise<{ isValid: boolean; error?: string; preview?: CSVPreviewRow[] }>;
  uploading: boolean;
  updating: boolean;
  error: string | null;
}

const REQUIRED_COLUMNS = ['name', 'linkedin_url'];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB

export function useTargetAudience(): UseTargetAudienceReturn {
  const [uploading, setUploading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Using direct supabase import instead of useSupabaseClient

  const validateCSVFile = useCallback(async (file: File): Promise<{ isValid: boolean; error?: string; preview?: CSVPreviewRow[] }> => {
    setError(null);

    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return { isValid: false, error: 'File size must be under 10MB' };
    }

    // Check file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      return { isValid: false, error: 'File must be a CSV' };
    }

    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            resolve({ isValid: false, error: 'CSV must contain at least a header row and one data row' });
            return;
          }

          // Parse header
          const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
          
          // Check required columns
          const missingColumns = REQUIRED_COLUMNS.filter(col => !headers.includes(col));
          if (missingColumns.length > 0) {
            resolve({ 
              isValid: false, 
              error: `Missing required columns: ${missingColumns.join(', ')}. Required: ${REQUIRED_COLUMNS.join(', ')}` 
            });
            return;
          }

          // Create preview (first 5 rows)
          const preview: CSVPreviewRow[] = lines.slice(0, Math.min(6, lines.length)).map((line, index) => {
            const values = line.split(',').map(v => v.trim());
            const row: CSVPreviewRow = { row: index === 0 ? 'Header' : index };
            headers.forEach((header, i) => {
              row[header] = values[i] || '';
            });
            return row;
          });

          resolve({ isValid: true, preview });
        } catch {
          resolve({ isValid: false, error: 'Failed to parse CSV file' });
        }
      };
      reader.onerror = () => resolve({ isValid: false, error: 'Failed to read file' });
      reader.readAsText(file);
    });
  }, []);

  const uploadCSV = useCallback(async (file: File, campaignId: string): Promise<string> => {
    setUploading(true);
    setError(null);

    try {
      // Validate file first
      const validation = await validateCSVFile(file);
      if (!validation.isValid) {
        throw new Error(validation.error);
      }

      // Create unique filename
      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `campaigns/${campaignId}/target_audience_${timestamp}_${sanitizedFileName}`;

      // Upload to Supabase Storage
      const { error } = await supabase.storage
        .from('campaign-files')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        throw new Error(`Upload failed: ${error.message}`);
      }

      // Get public URL
      const { data: urlData } = supabase.storage
        .from('campaign-files')
        .getPublicUrl(fileName);

      return urlData.publicUrl;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [validateCSVFile]);

  const updateCampaignTargetAudience = useCallback(async (campaignId: string, data: TargetAudienceData) => {
    setUpdating(true);
    setError(null);

    try {
      const updateData: {
        target_audience_type: 'csv_upload' | 'generic_filter' | null;
        target_audience_filter: TargetAudienceFilter | null;
        target_audience_file_url: string | null;
      } = {
        target_audience_type: data.type || null,
        target_audience_filter: data.filter || null,
        target_audience_file_url: data.fileUrl || null
      };

      const { error } = await supabase
        .from('campaigns')
        .update(updateData)
        .eq('id', campaignId);

      if (error) {
        throw new Error(`Failed to update campaign: ${error.message}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Update failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setUpdating(false);
    }
  }, []);

  return {
    uploadCSV,
    updateCampaignTargetAudience,
    validateCSVFile,
    uploading,
    updating,
    error
  };
}
